import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@/components/shared/Banner';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { RouteErrorBoundary } from '@/components/shared/RouteErrorBoundary';
import { Separator } from '@/components/ui/separator';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import {
  isUnauthenticated,
  redirectToSignIn,
  useIsArchive,
  useSession,
} from '@/features/auth/session';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api-client';
import { trackSessionActivity } from '@/lib/session-activity';
import { scheduleSessionExpiry } from '@/lib/session-expiry';
import { useLocale } from '@/hooks/use-locale';

/**
 * Rule CR2: a boundary at the `_app` layout itself, scoped to re-navigating
 * to the current URL rather than a full reload, so a failure in the shell
 * does not force the user to redo filters or navigation.
 */
function AppErrorBoundary({ error }: { error: unknown }) {
  const router = useRouter();
  return (
    <RouteErrorBoundary
      error={error}
      boundary="app-shell"
      onRetry={() => void router.invalidate()}
    />
  );
}

function AppLayout(): React.JSX.Element | null {
  const session = useSession();
  const queryClient = useQueryClient();

  // unified-access.md section 3.1 (SH-28): once the time left that the server
  // reported has passed and no save is in flight, ask the server again. Only
  // its 401 ends the session (the effect below then returns to Unified Access
  // with this page); a still-valid answer re-arms the timer. The device clock
  // is never compared with the server's expiry time.
  const { refetch, dataUpdatedAt } = session;
  const expiresInMs = session.data?.expiresInMs;
  useEffect(
    () =>
      scheduleSessionExpiry(
        expiresInMs,
        dataUpdatedAt,
        () => void refetch(),
        () => queryClient.isMutating() > 0,
      ),
    [expiresInMs, dataUpdatedAt, refetch, queryClient],
  );

  // unified-access.md section 3.1 (KAN-50, SH-29): the employee's clicks and
  // key presses keep the session open; reads and refetches never do.
  const signedIn = !!session.data;
  useEffect(
    () =>
      signedIn
        ? trackSessionActivity(() =>
            apiRequest<void>('/auth/dashboard/activity', { method: 'POST' }),
          )
        : undefined,
    [signedIn],
  );
  const { isRtl } = useLocale();
  const archive = useIsArchive();
  const { t } = useTranslation('common');

  useEffect(() => {
    // docs/rebuild/unified-access.md section 3, step 5: no session sends the
    // browser to Unified Access with a return address.
    if (!session.isPending && isUnauthenticated(session.error)) {
      redirectToSignIn();
    }
  }, [session.isPending, session.error]);

  if (session.isPending) return null;
  if (isUnauthenticated(session.error)) return null;
  if (session.error) throw session.error;

  return (
    <SidebarProvider>
      <AppSidebar session={session.data} />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className={`flex items-center gap-2 px-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <SidebarTrigger className="-ms-1" />
            <Separator orientation="vertical" className="me-2 h-4" />
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
          {/* fuel.md section 5.11: the archive environment is read-only. */}
          {archive ? (
            <Banner tone="info" message={t('archive.banner')} className="sticky top-0 z-10" />
          ) : null}
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

/** Everything under this layout requires a session, so a new page added below it is protected by default. */
export const Route = createFileRoute('/_app')({
  component: AppLayout,
  errorComponent: AppErrorBoundary,
});
